import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ApiError } from '@/api/http-client';
import {
  getChamadosControllerDetalharQueryKey,
  getChamadosControllerListarMensagensQueryKey,
  useChamadosControllerCancelar,
  useChamadosControllerDetalhar,
  useChamadosControllerEnviarAnexo,
  useChamadosControllerEnviarMensagem,
  useChamadosControllerListarMensagens,
  useChamadosControllerMarcarComoLido,
} from '@/api/generated/suporte/suporte';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { CartaoDePrazos } from '@/features/suporte/components/CartaoDePrazos';
import { ComposerDeMensagem } from '@/features/suporte/components/ComposerDeMensagem';
import { ThreadDeMensagens } from '@/features/suporte/components/ThreadDeMensagens';
import {
  CLASSE_STATUS,
  CLASSE_URGENCIA,
  estaEncerrado,
  estaVivo,
  formatarDataHora,
  ROTULO_STATUS,
  ROTULO_URGENCIA,
} from '@/features/suporte/rotulos';
import { INTERVALO_CHAT_MS, MENSAGENS_NA_CONVERSA } from '@/features/suporte/suporte.constants';
import { EstadoCarregando, EstadoErro } from '@/shared/components/estados';
import { cn } from '@/shared/lib/cn';
import { ROTAS } from '@/shared/lib/rotas';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { toast } from '@/shared/ui/sonner';

/** Um chamado, do ponto de vista de quem o abriu. */
export function ChamadoDetalhePage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const [confirmandoCancelar, setConfirmandoCancelar] = useState(false);

  const {
    data: chamado,
    isPending,
    isError,
    refetch,
  } = useChamadosControllerDetalhar(id, {
    query: { refetchInterval: INTERVALO_CHAT_MS },
  });

  const vivo = estaVivo(chamado?.status);

  const { data: mensagens } = useChamadosControllerListarMensagens(
    id,
    { pagina: 1, porPagina: MENSAGENS_NA_CONVERSA, ordenarPor: 'criadoEm', ordem: 'desc' },
    {
      query: {
        enabled: Boolean(chamado),
        // Chamado encerrado nunca mais muda — manter o poll seria um request a
        // cada 2s para sempre receber a mesma resposta.
        refetchInterval: vivo ? INTERVALO_CHAT_MS : false,
      },
    },
  );

  const enviarMensagem = useChamadosControllerEnviarMensagem();
  const enviarAnexo = useChamadosControllerEnviarAnexo();
  const marcarComoLido = useChamadosControllerMarcarComoLido();
  const cancelar = useChamadosControllerCancelar();

  // Marca como lido sempre que chega novidade com a tela aberta. Depende de
  // `naoLidas` e não de `[]`: o polling traz mensagem nova enquanto a pessoa
  // lê, e sem isto o badge da sidebar ficaria aceso com a conversa à vista.
  useEffect(() => {
    if (chamado && chamado.naoLidas > 0) void marcarComoLido.mutateAsync({ id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, chamado?.naoLidas]);

  async function aoEnviar({ corpo, arquivo }: { corpo: string; arquivo: File | null }) {
    try {
      const arquivoId = arquivo
        ? (await enviarAnexo.mutateAsync({ data: { arquivo } })).arquivoId
        : undefined;

      await enviarMensagem.mutateAsync({
        id,
        data: { corpo, clientMsgId: crypto.randomUUID(), arquivoId },
      });

      await queryClient.invalidateQueries({
        queryKey: getChamadosControllerListarMensagensQueryKey(id),
      });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível enviar a mensagem.');
      // Relança para o composer restaurar o texto — ver o comentário lá.
      throw e;
    }
  }

  async function aoCancelar() {
    try {
      await cancelar.mutateAsync({ id });
      await queryClient.invalidateQueries({ queryKey: getChamadosControllerDetalharQueryKey(id) });
      toast.success('Chamado cancelado.');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível cancelar o chamado.');
    } finally {
      setConfirmandoCancelar(false);
    }
  }

  if (isPending) return <EstadoCarregando rotulo="Carregando chamado..." />;
  if (isError || !chamado) return <EstadoErro aoTentarDeNovo={() => void refetch()} />;

  const encerrado = estaEncerrado(chamado.status);
  const podeCancelar = chamado.status === 'ABERTO' && chamado.solicitanteId === me?.usuario.id;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link
        to={ROTAS.suporte}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-brand"
      >
        <ArrowLeft className="h-4 w-4" />
        Meus chamados
      </Link>

      <header className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <p className="font-mono text-xs text-muted-foreground">{chamado.codigo}</p>
            <h1 className="text-xl font-semibold">{chamado.titulo}</h1>
          </div>

          {podeCancelar && (
            <Button variant="outline" size="sm" onClick={() => setConfirmandoCancelar(true)}>
              Cancelar chamado
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={cn('rounded border px-2 py-0.5', CLASSE_STATUS[chamado.status])}>
            {ROTULO_STATUS[chamado.status]}
          </span>
          <span className={cn('rounded border px-2 py-0.5', CLASSE_URGENCIA[chamado.urgencia])}>
            Urgência {ROTULO_URGENCIA[chamado.urgencia]}
          </span>
          <span className="text-muted-foreground">
            Aberto em {formatarDataHora(chamado.abertoEm)}
          </span>
          {chamado.atendenteNome && (
            <span className="text-muted-foreground">· Atendido por {chamado.atendenteNome}</span>
          )}
        </div>
      </header>

      <CartaoDePrazos chamado={chamado} />

      <div className="rounded-lg border border-brand/15 bg-surface-raised p-4">
        <ThreadDeMensagens
          mensagens={[...(mensagens?.itens ?? [])].reverse()}
          meuProfileId={me?.usuario.id}
        />
      </div>

      {encerrado ? (
        <div className="space-y-2 rounded-lg border border-emerald-600/30 bg-emerald-600/5 p-4">
          <p className="flex items-center gap-2 text-sm font-medium">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            {chamado.status === 'CONCLUIDO' ? 'Chamado concluído' : 'Chamado cancelado'}
          </p>
          {chamado.resolucao && (
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{chamado.resolucao}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Se o problema voltar, abra um chamado novo e cite o código {chamado.codigo}.
          </p>
        </div>
      ) : (
        <ComposerDeMensagem
          aoEnviar={aoEnviar}
          enviando={enviarMensagem.isPending || enviarAnexo.isPending}
          placeholder={
            chamado.status === 'ABERTO'
              ? 'Quer acrescentar alguma informação?'
              : 'Escreva uma mensagem...'
          }
        />
      )}

      <AlertDialog open={confirmandoCancelar} onOpenChange={setConfirmandoCancelar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar este chamado?</AlertDialogTitle>
            <AlertDialogDescription>
              Ele sai da fila do suporte e ninguém mais vai atendê-lo. A ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void aoCancelar()}>
              Cancelar chamado
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
